import { api } from '@/helpers/api'
import { commonUtils } from '@/helpers/commonUtils'
import { useCallback, useEffect, useState } from 'react'
import type { BiluEntryType } from '@/helpers/api/user'

export interface WechatBiluLaunchInfo {
  username: string
  path: string
}

interface BiluEntryPayload {
  url: string
  wxMnpId?: string
}

interface UseWechatBiluLaunchInfoResult {
  canUseWechatLaunch: boolean
  launchInfo?: WechatBiluLaunchInfo
  launchRenderKey: number
}

let wechatOpenTagConfigPromise: Promise<void> | null = null
let wechatOpenTagConfigUrl = ''
const WECHAT_SIGN_FULL_URL_STORAGE_KEY = 'wechat_sign_full_url'

function resetWechatOpenTagConfigState(): void {
  wechatOpenTagConfigPromise = null
  wechatOpenTagConfigUrl = ''
}

function getWechatSignUrl(): string {
  console.log('[wechat-sign] capture url', window.location.href)
  const currentUrl = window.location.href.split('#')[0]

  try {
    const current = new URL(currentUrl)
    const currentCode = current.searchParams.get('code')

    if (currentCode) {
      sessionStorage.setItem(WECHAT_SIGN_FULL_URL_STORAGE_KEY, currentUrl)
      return currentUrl
    }

    const storedUrl = sessionStorage.getItem(WECHAT_SIGN_FULL_URL_STORAGE_KEY)
    if (!storedUrl) {
      return currentUrl
    }

    const stored = new URL(storedUrl)
    const currentState = current.searchParams.get('state')
    const storedState = stored.searchParams.get('state')
    const isSamePage =
      stored.origin === current.origin &&
      stored.pathname === current.pathname &&
      currentState === storedState

    return isSamePage ? storedUrl : currentUrl
  } catch (_error) {
    return currentUrl
  }
}

export async function getBiluEntryPayload(
  scene: 'wechat_h5' | 'mobile_browser',
  entry: BiluEntryType
): Promise<BiluEntryPayload | undefined> {
  const { data } = await api.user.getBiluSsoEntry(scene, entry)
  const responseData = typeof data === 'string' ? { url: data } : data
  const targetUrl = responseData?.url

  if (!targetUrl) {
    return undefined
  }

  return {
    url: targetUrl,
    wxMnpId: responseData?.wxMnpId,
  }
}

async function ensureWechatOpenTagConfigReady(): Promise<void> {
  // const currentUrl = window.location.href.split('#')[0]
  const signUrl = getWechatSignUrl()

  // console.log('[wechat-jssdk] preparing wx.config', {
  //   currentUrl,
  //   signUrl,
  // })

  if (wechatOpenTagConfigPromise && wechatOpenTagConfigUrl === signUrl) {
    return wechatOpenTagConfigPromise
  }

  wechatOpenTagConfigUrl = signUrl
  wechatOpenTagConfigPromise = (async () => {
    const wxModule = await import('weixin-js-sdk')
    const wx = (wxModule.default || wxModule) as any
    console.log('[wechat-jssdk] request signature with url', signUrl)
    const { data: config } = await api.user.getWechatJsSdkConfig({
      url: signUrl,
    })

    wx.config({
      debug: false,
      appId: config.appId,
      timestamp: config.timestamp,
      nonceStr: config.nonceStr,
      signature: config.signature,
      jsApiList: [],
      openTagList: ['wx-open-launch-weapp'],
    })

    await new Promise<void>((resolve, reject) => {
      wx.ready(() => {
        resolve()
      })

      wx.error((error: unknown) => {
        console.error(error)
        reject(error)
      })
    })
  })().catch((error) => {
    resetWechatOpenTagConfigState()
    throw error
  })

  return wechatOpenTagConfigPromise
}

export function useWechatBiluLaunchInfo(
  enabled: boolean,
  isLoggedIn: boolean,
  entry: BiluEntryType
): UseWechatBiluLaunchInfoResult {
  const [launchInfo, setLaunchInfo] = useState<WechatBiluLaunchInfo | undefined>(undefined)
  const [launchRenderKey, setLaunchRenderKey] = useState(0)
  const canRefreshLaunchInfo = Boolean(enabled && isLoggedIn && commonUtils.isWechat())

  const refreshLaunchInfo = useCallback(async (): Promise<WechatBiluLaunchInfo | undefined> => {
    if (!canRefreshLaunchInfo) {
      setLaunchInfo(undefined)
      return undefined
    }

    try {
      await ensureWechatOpenTagConfigReady()
      const payload = await getBiluEntryPayload('wechat_h5', entry)

      if (!payload?.url || !payload.wxMnpId) {
        setLaunchInfo(undefined)
        return undefined
      }

      const nextLaunchInfo = {
        username: payload.wxMnpId,
        path: payload.url,
      }

      setLaunchInfo(nextLaunchInfo)
      setLaunchRenderKey((prev) => prev + 1)
      return nextLaunchInfo
    } catch (error) {
      setLaunchInfo(undefined)
      console.error('init wechat bilu launch failed', error)
      return undefined
    }
  }, [canRefreshLaunchInfo, entry])

  useEffect(() => {
    if (!canRefreshLaunchInfo) {
      setLaunchInfo(undefined)
      return
    }

    let disposed = false

    const initWechatLaunchInfo = async (): Promise<void> => {
      try {
        const nextLaunchInfo = await refreshLaunchInfo()

        if (!disposed && !nextLaunchInfo) {
          setLaunchInfo(undefined)
        }
      } catch (error) {
        if (!disposed) {
          setLaunchInfo(undefined)
        }
        console.error('init wechat bilu launch failed', error)
      }
    }

    const handlePageShow = (event: PageTransitionEvent): void => {
        console.log('page show',event)
      resetWechatOpenTagConfigState()
      void initWechatLaunchInfo()
    }

    initWechatLaunchInfo()

    const refreshTimer = window.setInterval(() => {
      void refreshLaunchInfo()
    }, 6 * 1000)
    window.addEventListener('pageshow', handlePageShow)

    return () => {
      disposed = true
      window.clearInterval(refreshTimer)
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [canRefreshLaunchInfo, refreshLaunchInfo])

  return {
    canUseWechatLaunch:
      canRefreshLaunchInfo &&
      !!launchInfo?.username &&
      !!launchInfo?.path,
    launchInfo,
    launchRenderKey,
  }
}
